import { z } from 'zod/v4';

/**
 * Body accepted by `POST /users`. Faithful port of the legacy `user.create`
 * JSON Schema (`backend/src/2025-06/api-service/routes/schemas/v3/request/user.js`),
 * extended with an optional `roleKey` so an inviter can pick any `iam_roles`
 * row for the target tenant (including a custom role built via the Permission
 * Sets UI) instead of the implicit company-admin/company-creator derivation. Omitting
 * it preserves that derivation (see `UserCreationService.create`).
 */
export const CreateUserBodySchema = z.object({
  email: z.email().nullable().optional(),
  companyId: z.string().min(1),
  firstName: z
    .string()
    .min(1)
    .max(255)
    .regex(/^\S.*/, 'firstName must not start with whitespace'),
  lastName: z.string().nullable().optional(),
  /** An `iam_roles.key` for `companyId`'s tenant. Must exist in that tenant. */
  roleKey: z.string().min(1).optional(),
});

export type CreateUserBody = z.infer<typeof CreateUserBodySchema>;

/**
 * Row shape returned by `POST /users` — the raw (unenriched) user row, same
 * as legacy `usersDB.getByIdUnScoped`. `looseObject` because the underlying
 * `users` table has ~25 columns (including audit columns) and this response
 * doesn't need strict validation of all of them.
 */
export const CreateUserResponseSchema = z.looseObject({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  primaryCompanyId: z.string().nullable(),
  langId: z.string().nullable(),
  isBlocked: z.number().nullable(),
  intercomHash: z.string().nullable(),
  emailSha256: z.string().nullable(),
  avatarFileUrl: z.string().nullable(),
  originalAvatarFileUrl: z.string().nullable(),
});

export type CreateUserResponse = z.infer<typeof CreateUserResponseSchema>;
